import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPortalOrders, portalCancelMyOrder } from '../../services/api';
import { Package, Car, Truck, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PortalOrders = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadOrders = useCallback(async () => {
    try {
      const res = await getPortalOrders();
      setOrders(res.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const statusLabels = {
    new_lead: t('status.new_lead'),
    waiting_documents: t('status.waiting_documents'),
    documents_received: t('status.documents_received'),
    car_sourcing: t('status.car_sourcing'),
    vehicle_confirmed: t('status.vehicle_confirmed'),
    payment_pending: t('status.payment_pending'),
    payment_received: t('status.payment_received'),
    shipping_booked: t('status.shipping_booked'),
    in_transit: t('status.in_transit'),
    container_loaded: t('status.container_loaded'),
    bl_issued: t('status.bl_issued'),
    arrived_algeria: t('status.arrived_algeria'),
    customs_clearance: t('status.customs_clearance'),
    ready_delivery: t('status.ready_delivery'),
    delivered: t('status.delivered'),
    cancelled: t('status.cancelled')
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['delivered', 'cancelled'].includes(order.status);
    if (filter === 'transit') return ['in_transit', 'container_loaded', 'bl_issued'].includes(order.status);
    if (filter === 'completed') return order.status === 'delivered';
    return true;
  });

  const filters = [
    { id: 'all', label: 'All Orders', count: orders.length },
    { id: 'active', label: 'Active', count: orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length },
    { id: 'transit', label: 'In Transit', count: orders.filter(o => ['in_transit', 'container_loaded', 'bl_issued'].includes(o.status)).length },
    { id: 'completed', label: 'Delivered', count: orders.filter(o => o.status === 'delivered').length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="portal-orders">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">{t('nav.myOrders')}</h1>
        <p className="text-muted-foreground mt-1">Track and manage your car import orders</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filter === f.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
            }`}
            data-testid={`filter-${f.id}`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="card-brutalist text-center py-12">
          <Package weight="duotone" className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-lg font-medium">No orders found</p>
          <p className="text-muted-foreground">
            {filter !== 'all' ? 'Try changing your filter' : 'Your orders will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order, idx) => (
            <Link
              key={order.id}
              to={`/portal/orders/${order.id}`}
              className={`card-brutalist block animate-fade-in stagger-${(idx % 5) + 1}`}
              data-testid={`order-card-${order.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-secondary flex items-center justify-center flex-shrink-0">
                    {['in_transit', 'container_loaded', 'bl_issued'].includes(order.status) ? (
                      <Truck weight="duotone" className="w-7 h-7" />
                    ) : order.vehicle ? (
                      <Car weight="duotone" className="w-7 h-7" />
                    ) : (
                      <Package weight="duotone" className="w-7 h-7" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold">{order.order_number}</span>
                      <span className={`status-badge status-${order.status}`}>
                        {statusLabels[order.status] || order.status}
                      </span>
                    </div>
                    {order.vehicle && (
                      <p className="text-sm font-medium">
                        {order.vehicle.brand_name} {order.vehicle.model_name} ({order.vehicle.year})
                      </p>
                    )}
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-1">
                      {order.container_number && (
                        <span className="font-mono">Container: {order.container_number}</span>
                      )}
                      {order.estimated_arrival && (
                        <span>ETA: {order.estimated_arrival}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {order.source === 'dealer_portal' && order.status === 'new_order' && (
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const reason = window.prompt('Cancel this order? Optional reason:');
                        if (reason === null) return;
                        try {
                          await portalCancelMyOrder(order.id, reason || '');
                          toast.success('Order cancelled — our team has been notified');
                          loadOrders();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Cancel failed');
                        }
                      }}
                      className="text-xs text-red-700 hover:underline"
                      data-testid={`portal-cancel-order-${order.id}`}
                    >
                      Cancel
                    </button>
                  )}
                  <div className="flex items-center text-primary font-medium">
                    View Details
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalOrders;
